import React, { useState, useEffect } from 'react'
import { api, formatDate } from '../../utils/api'
import { dueLabel, blockedReason, displayName, daysLeft } from './accountDeletionRows.js'

const ENDPOINT = '/api/marketing/account-deletions'
//: Urgent shows in a word too ("2 days overdue"), so colour never carries it alone.
const MUTED = { color: 'var(--color-text-muted)' }
const URGENT = { color: 'var(--color-danger, #8E2420)', fontWeight: 600 }

/**
 * Marketing > Account deletions (admin only).
 *
 * Customers who asked to delete their account. UK GDPR gives us one month from
 * the request. "Complete" calls Shopify's customer data erasure, queues the
 * Klaviyo deletion and removes our own copies. Quotes, payments and tax records
 * stay, as the customer was told.
 */
function AccountDeletions() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [working, setWorking] = useState(null)
  const [done, setDone] = useState(null)

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get(ENDPOINT)
      setRows(res.requests || [])
    } catch (e) {
      setError(e.message || 'Could not load deletion requests')
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  async function complete(row) {
    const ok = window.confirm(
      `Delete the account for ${displayName(row)}? This erases their details and cannot be undone.`
    )
    if (!ok) return
    setWorking(row.request_id)
    setError(null)
    try {
      await api.post(`${ENDPOINT}/${row.request_id}/complete`, {})
      setDone(`Deletion completed for ${displayName(row)}.`)
      await load()
    } catch (e) {
      setError(e.message || 'Could not complete the deletion')
    } finally {
      setWorking(null)
    }
  }

  if (loading) return <div className="card" role="status">Loading deletion requests</div>

  return (
    <div>
      {error && <div className="card" role="alert" style={{ marginBottom: 'var(--space-4)' }}>{error}</div>}
      {done && <div className="card" role="status" style={{ marginBottom: 'var(--space-4)' }}>{done}</div>}
      {rows.length === 0 ? (
        <div className="card">No open deletion requests.</div>
      ) : (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Asked</th>
                <th>Due</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const reason = blockedReason(row)
                return (
                  <tr key={row.request_id}>
                    <td>
                      <div>{displayName(row)}</div>
                      <div style={MUTED}>{row.email || ''}</div>
                    </td>
                    <td>{formatDate(row.requested_at)}</td>
                    <td>
                      <div>{formatDate(row.due_at)}</div>
                      <div style={daysLeft(row.due_at) <= 3 ? URGENT : MUTED}>
                        {dueLabel(row.due_at)}
                      </div>
                    </td>
                    <td>{reason || 'Ready to complete'}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-danger"
                        disabled={reason !== null || working === row.request_id}
                        onClick={() => complete(row)}
                      >
                        {working === row.request_id ? 'Deleting' : 'Complete deletion'}
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default AccountDeletions
